import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getGetBackupReminderQueryKey,
  useCompleteBackupReminder,
  useListItems,
  type Item,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  BackupImageError,
  downloadFullBackup,
  type BackupImageFailure,
  type BackupProgress,
} from "@/lib/backup-export";

type BackupDownloadState = {
  status: "idle" | "downloading" | "success" | "error";
  progress: BackupProgress | null;
  message: string | null;
  failures: BackupImageFailure[];
};

type BackupDownloadContextValue = BackupDownloadState & {
  items: Item[] | undefined;
  itemsLoading: boolean;
  startDownload: () => Promise<boolean>;
  resetStatus: () => void;
};

const BackupDownloadContext = createContext<BackupDownloadContextValue | null>(
  null,
);

const initialState: BackupDownloadState = {
  status: "idle",
  progress: null,
  message: null,
  failures: [],
};

export function BackupDownloadProvider({ children }: { children: ReactNode }) {
  const { data: items, isLoading: itemsLoading } = useListItems();
  const queryClient = useQueryClient();
  const completeReminder = useCompleteBackupReminder();
  const [state, setState] = useState<BackupDownloadState>(initialState);
  const downloadActiveRef = useRef(false);

  const startDownload = async (): Promise<boolean> => {
    if (downloadActiveRef.current) return false;
    if (!items?.length) {
      setState({
        status: "error",
        progress: null,
        message: "Add at least one collection entry before creating a backup.",
        failures: [],
      });
      return false;
    }

    downloadActiveRef.current = true;
    setState({
      status: "downloading",
      progress: { stage: "preparing", completed: 0, total: 0 },
      message: null,
      failures: [],
    });

    try {
      const filename = await downloadFullBackup(items, (progress) => {
        setState((current) => ({ ...current, progress }));
      });
      const reminder = await completeReminder.mutateAsync();
      queryClient.setQueryData(getGetBackupReminderQueryKey(), reminder);
      setState({
        status: "success",
        progress: null,
        message: `${filename} was downloaded successfully.`,
        failures: [],
      });
      return true;
    } catch (error) {
      const failures =
        error instanceof BackupImageError ? error.failures : [];
      setState({
        status: "error",
        progress: null,
        message:
          error instanceof Error
            ? error.message
            : "The backup could not be created. Please try again.",
        failures,
      });
      return false;
    } finally {
      downloadActiveRef.current = false;
    }
  };

  const value = useMemo(
    () => ({
      ...state,
      items,
      itemsLoading,
      startDownload,
      resetStatus: () => setState(initialState),
    }),
    [items, itemsLoading, state],
  );

  return (
    <BackupDownloadContext.Provider value={value}>
      {children}
    </BackupDownloadContext.Provider>
  );
}

export function useBackupDownload() {
  const context = useContext(BackupDownloadContext);
  if (!context) {
    throw new Error(
      "useBackupDownload must be used within BackupDownloadProvider",
    );
  }
  return context;
}